import { z } from "zod";
import { ZBoundingBox2D, ZObjectIdString, ZPolygon2D, ZProjectNo } from "./foundationSchema";
import { ZMLPredictDevice, ZMLPredictionMetadata, ZMlCeilingGridIntersection } from "./mlSchema";
import { ZUserRef } from "./user.schema";
import { ZRoomValidationSpecifiedArticles } from "./room/specifiedArticlesSchema";
import { ZRoomValidationDiffItem, ZRoomValidationSummary, ZRoomValidationSummaryLite, ZRoomValidationSummaryOnly, } from "./room/roomValidationSchema";
import { ZRoomNumberingMethodKey } from "../roomNumbering/roomNumberingMethods";
export const ROOM_VALIDATION_STATUS_CODES = [
    "APPROVED",
    "APPROVED_WITH_DEVIATIONS",
    "NEEDS_CHECK",
];
export const ROOM_VALIDATION_STATUS_LABELS = {
    APPROVED: "Godkjent",
    APPROVED_WITH_DEVIATIONS: "Godkjent med avvik",
    NEEDS_CHECK: "Må kontrolleres",
};
export const ZDateTime = z.string().datetime().or(z.date());
export const ZDateTimeOpt = ZDateTime.optional();
export const ZDateTimeNull = ZDateTime.nullable();
export const ZDateTimeNullish = ZDateTime.nullish();
export const ZComment = z.object({
    _id: ZObjectIdString,
    message: z.string().min(1).max(2000),
    createdBy: ZUserRef,
    createdAt: ZDateTime,
    editedAt: ZDateTimeNullish.optional(),
    deletedAt: ZDateTimeNullish.optional(),
});
export const ZRoomValidationStandardRoomDiscipline = z.object({
    standardRoomId: z.string().min(1),
    summary: ZRoomValidationSummary,
    comparison: ZRoomValidationDiffItem.array().optional().default([]),
    engineVersion: z.string().min(1).optional(),
    sourceFingerprint: z.string().min(1).optional(),
    validatedAt: z.string().datetime(),
    validatedBy: z.string().min(1).default("system:standardroom"),
    note: z.string().optional(),
});
export const ZRoomValidationStandardRoom = z.record(z.string().min(1), ZRoomValidationStandardRoomDiscipline);
export const ZRoomValidation = z.object({
    standardRoom: ZRoomValidationStandardRoom.optional(),
    specifiedArticles: ZRoomValidationSpecifiedArticles.optional(),
});
export const ZRoomValidationStatusCode = z.enum(ROOM_VALIDATION_STATUS_CODES);
export const ZRoomValidationStatusEntry = z.object({
    status: ZRoomValidationStatusCode,
    comment: z.string().max(2000).nullable().optional(),
    updatedByUserId: ZObjectIdString,
    updatedByUserName: z.string(),
    updatedAt: ZDateTime,
});
export const ZRoomValidationStatusState = z.object({
    current: ZRoomValidationStatusEntry.nullable().optional(),
    history: z.array(ZRoomValidationStatusEntry).default([]),
    comments: z.array(ZComment).default([]),
});
export const ZRoomValidationDecisions = z.object({
    standardRoom: z.record(z.string(), ZRoomValidationStatusState).optional(),
});
const ZRoomExtraIfcInfo = z.object({
    pns: z.string().nullable().optional(),
    floor: z.string().nullable().optional(),
    mmi: z.string().nullable().optional(),
    ceilingMmi: z.string().nullable().optional(),
    building: z.string().nullable().optional(),
});
export const ZRoomSpecifiedArticlesSync = z.object({
    syncedAt: ZDateTime,
    source: z.string().optional(),
    version: z.string().optional(),
});
export const ZRoomSpecifiedArticle = z.object({
    compCode: z.string(),
    quantity: z.number(),
    name: z.string().optional(),
    responsibility: z.string().optional(),
    category: z.string().optional(),
    flags: z
        .object({
        toBeDrawn: z.boolean().optional(),
        bip: z.boolean().optional(),
    })
        .optional(),
    sourceRef: z
        .object({
        system: z.string().optional(),
        id: z.string().optional(),
    })
        .optional(),
});
export const ZBBoxSnapshot = z.object({
    bbox: ZBoundingBox2D,
    bboxHash: z.string(),
    hashVersion: z.literal(1),
});
export const ZRoomMcBimItem = z.object({
    globalId: z.string(),
    discipline: z.string().optional().nullable(),
    system: z.string().optional().nullable(),
    name: z.string().optional().nullable(),
    typeName: z.string().optional().nullable(),
    instanceClass: z.string(),
    mmi: z.string().optional().nullable(),
    compCode: z.string().optional().nullable(),
    bboxSnapshot: ZBBoxSnapshot.optional().nullable(),
    building: z.string().optional().nullable(),
});
export const ZRoomMcBimItemsSnapshotMeta = z.object({
    builtAt: ZDateTime,
    runId: ZObjectIdString,
    sourceKey: z.string(),
});
export const ZRoomMcBimItemsSnapshot = z.object({
    meta: ZRoomMcBimItemsSnapshotMeta,
    items: z.array(ZRoomMcBimItem),
});
export const ZPolygonSnapshot = z.object({
    polygon: ZPolygon2D,
    polygonHash: z.string(),
});
export const ZRoomCeilingFreeTile = z.object({
    gridIndex: z.number().int().min(0),
    rowIndex: z.number().int().min(0),
    columnIndex: z.number().int().min(0),
    boundingBox: ZBoundingBox2D,
});
export const ZRoomNumberingApproval = z.object({
    approvedBy: ZUserRef,
    approvedAt: ZDateTime,
});
export const ZRoomNumberingRun = z.object({
    methodKey: ZRoomNumberingMethodKey,
    runBy: ZUserRef,
    runAt: ZDateTime,
});
export const ZRoomNumbering = z.object({
    suggested: z.string().optional(),
    run: ZRoomNumberingRun.optional(),
    value: z.string().optional(),
    updatedBy: ZUserRef.optional(),
    updatedAt: ZDateTime.optional(),
    approval: ZRoomNumberingApproval.optional(),
});
export const ZAirFlowSource = z.enum(["excel", "manual"]);
export const ZAirFlowSourceRef = z.object({
    system: z.string().optional(),
    id: z.string().optional(),
    fileName: z.string().optional(),
    importId: z.string().optional(),
});
const ZAirFlowValues = z.object({
    supply: z.number().min(0).optional(),
    exhaust: z.number().min(0).optional(),
    comment: z.string().max(2000).optional(),
});
export const ZSelectedAirFlowValue = z.object({
    value: z.number().min(0),
    source: ZAirFlowSource,
    selectedAt: ZDateTime.optional(),
    selectedBy: ZUserRef.optional(),
});
export const ZAirFlowSelected = z.object({
    supply: ZSelectedAirFlowValue.optional(),
    exhaust: ZSelectedAirFlowValue.optional(),
});
export const ZExcelAirFlow = ZAirFlowValues.extend({
    sourceRef: ZAirFlowSourceRef.optional(),
    importedAt: ZDateTime.optional(),
    importedBy: ZUserRef.optional(),
});
export const ZManualAirFlow = ZAirFlowValues.extend({
    updatedAt: ZDateTime.optional(),
    updatedBy: ZUserRef.optional(),
});
export const ZAirFlow = z.object({
    selected: ZAirFlowSelected.optional(),
    excel: ZExcelAirFlow.optional(),
    manual: ZManualAirFlow.optional(),
    comments: z.array(ZComment).optional(),
});
export const ZRoom = z.object({
    _id: ZObjectIdString,
    projectNo: ZProjectNo,
    roomNumber: z.string(),
    roomNumbering: ZRoomNumbering.optional(),
    roomName: z.string().optional(),
    airFlow: ZAirFlow.optional(),
    ceilingComments: z.array(ZComment).default([]),
    supplyAirFlow: z.number().optional(),
    returnAirFlow: z.number().optional(),
    rfpStatus: z.string().nullable().optional(),
    rfpMalRom: z.string().nullable().optional(),
    drStatus: z.string().nullable().optional(),
    designDeviationStatus: z.string().nullable().optional(),
    updatedAt: ZDateTime.optional(),
    multiplacerCeilingDevicesLocked: z
        .object({
        bool: z.boolean(),
        lockedAt: ZDateTime.optional(),
        createdBy: ZUserRef.optional(),
    })
        .optional(),
    validation: ZRoomValidation.optional(),
    validationDecisions: ZRoomValidationDecisions.optional(),
    extraIfcInfo: ZRoomExtraIfcInfo.optional(),
    mlPredictedDevices: z.array(ZMLPredictDevice).optional(),
    mlPredictionMetadata: ZMLPredictionMetadata.optional(),
    existsInRfp: z.boolean().optional(),
    existsInMcbim: z.boolean().optional(),
    specifiedArticles: z.array(ZRoomSpecifiedArticle).optional(),
    specifiedArticlesSync: ZRoomSpecifiedArticlesSync.optional(),
    polygonSnapshot: ZPolygonSnapshot.optional(),
    ceilingGridIntersection: ZMlCeilingGridIntersection.optional(),
    ceilingFreeTiles: z.array(ZRoomCeilingFreeTile).optional(),
    mcbimItemsSnapshot: ZRoomMcBimItemsSnapshot.optional(),
});
export const ZRoomValidationStandardRoomDisciplineSummary = z.object({
    summary: ZRoomValidationSummaryLite,
});
export const ZRoomValidationStatusEntrySummary = z.object({
    status: ZRoomValidationStatusCode,
    updatedByUserName: z.string(),
    updatedAt: ZDateTime,
});
export const ZRoomValidationStatusStateSummary = z.object({
    current: ZRoomValidationStatusEntrySummary,
});
export const ZRoomValidationDecisionsSummary = z.object({
    standardRoom: z.record(z.string(), ZRoomValidationStatusStateSummary).optional(),
});
export const ZRoomSummary = ZRoom.pick({
    _id: true,
    projectNo: true,
    roomNumber: true,
    roomNumbering: true,
    roomName: true,
    airFlow: true,
    ceilingComments: true,
    supplyAirFlow: true,
    returnAirFlow: true,
    rfpStatus: true,
    rfpMalRom: true,
    drStatus: true,
    designDeviationStatus: true,
    updatedAt: true,
    multiplacerCeilingDevicesLocked: true,
    extraIfcInfo: true,
}).extend({
    validation: ZRoomValidationSummaryOnly.optional(),
    validationDecisions: ZRoomValidationDecisionsSummary.optional(),
});
export const ZRoomsSummaryHashmap = z.record(z.string(), ZRoomSummary);
